import { AccordionRoot } from "@design-system/Accordion/Accordion";
import { DetailsAccordionItem } from "@design-system/Accordion/DetailsAccordionItem";
import { DetailsAside } from "@design-system/DetailsAside/DetailsAside";
import { parseCloseId } from "@hooks/useOpenWorkRow";
import type { ClientsWorkOverviewWorkRow } from "../clientsWorkOverviewViewConfig";
import { useWorkRowActions } from "../WorkRowActions";
import { WorkActivitySection } from "./WorkActivitySection";
import { WorkDetailPaneHeader } from "./WorkDetailPaneHeader";
import { WorkDetailsSection } from "./WorkDetailsSection";
import { WorkNotificationsSection } from "./WorkNotificationsSection";
import styles from "./WorkDetailPane.module.scss";

interface WorkDetailPaneProps {
    row: ClientsWorkOverviewWorkRow;
    onClose: () => void;
}

/**
 * The work item's details beside the list or board: a header with its
 * actions, then Details, Notifications and Activity, collapsed. Their content
 * stays mounted while collapsed, so it has loaded and a section opens straight
 * to its full height. Switching items swaps the content in place and keeps
 * which sections are open; the per-item sections start over.
 */
export const WorkDetailPane = ({
    row,
    onClose,
}: Readonly<WorkDetailPaneProps>) => {
    const { onConfirmationRequest, confirmationModal } = useWorkRowActions();
    const closeId = parseCloseId(row.closeId) ?? row.closeId;

    return (
        <DetailsAside>
            <section className={styles.pane} aria-label="Work item details">
                <WorkDetailPaneHeader
                    row={row}
                    onClose={onClose}
                    onConfirmationRequest={onConfirmationRequest}
                />
                <div className={styles.content}>
                    <AccordionRoot multiple keepMounted>
                        <DetailsAccordionItem value="details" label="Details">
                            <WorkDetailsSection row={row} />
                        </DetailsAccordionItem>
                        <DetailsAccordionItem
                            value="notifications"
                            label="Notifications"
                        >
                            <WorkNotificationsSection
                                key={closeId}
                                closeId={closeId}
                                workspaceId={row.workspaceId}
                            />
                        </DetailsAccordionItem>
                        <DetailsAccordionItem value="activity" label="Activity">
                            <WorkActivitySection
                                key={closeId}
                                closeId={closeId}
                                workspaceId={row.workspaceId}
                            />
                        </DetailsAccordionItem>
                    </AccordionRoot>
                </div>
            </section>
            {confirmationModal}
        </DetailsAside>
    );
};
